import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    createContent,
    deleteContent,
    deleteMedia,
    getContentById,
    getContentList,
    updateContent,
} from "../api/contentApi";
import { useAsyncAction } from "./useAsyncAction";
import { showErrorToast, showSuccessToast } from "../utils/toast";

const getVideoThumbnail = (url) => {
    const match = url?.match(/videodelivery\.net\/([a-zA-Z0-9]+)/);
    return match ? `https://videodelivery.net/${match[1]}/thumbnails/thumbnail.jpg` : null;
};

const normalizeContentItem = (item) => {
    const type = item?.type || item?.fileType || "Image";
    const rawThumbnail = item?.thumbnail || item?.preview || "/assets/images/dashboard/D1.png";
    const thumbnail = type === "Video"
        ? (getVideoThumbnail(rawThumbnail) || rawThumbnail)
        : rawThumbnail;
    return {
    id: String(item?.id ?? ""),
    title: item?.title || "",
    date: String(item?.date || "").replace(/^posted on\s*/i, "").trim(),
    type,
    thumbnail,
    preview: item?.preview || item?.thumbnail || "/assets/images/dashboard/D1.png",
    platform: item?.platform || "",
    fileType: item?.fileType || "Image",
    postType: item?.postType || "Post",
    caption: item?.caption || "",
    link: item?.link || "",
    fileUrl: item?.fileUrl || item?.preview || "",
    cloudflareId: item?.cloudflareId || "",
    thumbnailCloudflareId: item?.thumbnailCloudflareId || "",
    };
};

export const emptyContentFormData = {
    title: "",
    platform: "",
    link: "",
    fileType: "",
    postType: "",
    caption: "",
    acceptedTerms: false,
    mediaUrl: "",
    cloudflareId: "",
    thumbnailUrl: "",
    thumbnailCloudflareId: "",
};

export const useMyContentData = (contentId = "", { skipInitialLoad = false } = {}) => {
    const navigate = useNavigate();
    const [showSuccess, setShowSuccess] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [items, setItems] = useState([]);
    const [showCreateConfirm, setShowCreateConfirm] = useState(false);
    const [pendingCreateValues, setPendingCreateValues] = useState(null);
    const [pendingMedia, setPendingMedia] = useState({
        cloudflareId: "",
        thumbnailCloudflareId: "",
        fileType: "",
    });
    const loadAction = useAsyncAction();
    const { run: runLoadContent } = loadAction;
    const createAction = useAsyncAction();
    const { run: runCreateContent } = createAction;
    const updateAction = useAsyncAction();
    const { run: runUpdateContent } = updateAction;
    const deleteAction = useAsyncAction();
    const { run: runDeleteContent } = deleteAction;
    const isLoading = loadAction.isLoading;
    const isSubmitting = createAction.isLoading || updateAction.isLoading;
    const isDeleting = deleteAction.isLoading;
    const apiError =
        createAction.errorMessage ||
        updateAction.errorMessage ||
        deleteAction.errorMessage ||
        loadAction.errorMessage;

    useEffect(() => {
        if (!apiError) return;
        showErrorToast(apiError);
    }, [apiError]);

    const cleanupPendingUploads = useCallback(async () => {
        const ids = [pendingMedia.cloudflareId, pendingMedia.thumbnailCloudflareId].filter(Boolean);
        const uniqueIds = [...new Set(ids)];
        const type = pendingMedia.fileType === "Video" ? "video" : "image";

        for (const id of uniqueIds) {
            try {
                await deleteMedia({
                    cloudflareId: id,
                    type: id === pendingMedia.thumbnailCloudflareId ? "image" : type,
                });
            } catch {}
        }

        setPendingMedia({ cloudflareId: "", thumbnailCloudflareId: "", fileType: "" });
    }, [pendingMedia.cloudflareId, pendingMedia.fileType, pendingMedia.thumbnailCloudflareId]);

    const loadContent = useCallback(async () => {
        const result = await runLoadContent(
            () => getContentList(),
            { errorFallback: "Unable to load content.", silentErrorToast: true }
        );
        const normalized = Array.isArray(result) ? result.map(normalizeContentItem) : [];
        setItems(normalized);
        return normalized;
    }, [runLoadContent]);

    const loadSelectedItem = useCallback(async (id) => {
        const result = await runLoadContent(
            () => getContentById(id),
            { errorFallback: "Unable to load content.", silentErrorToast: true }
        );
        if (result) {
            setSelectedItem(normalizeContentItem(result));
        }
    }, [runLoadContent]);

    useEffect(() => {
        if (skipInitialLoad) return;
        if (contentId) {
            loadSelectedItem(contentId);
        } else {
            loadContent();
        }
    }, [loadContent, loadSelectedItem, skipInitialLoad, contentId]);

    const openCreate = () => {
        setShowSuccess(false);
        navigate("/my-content/new");
    };

    const openList = async () => {
        await cleanupPendingUploads();
        setShowSuccess(false);
        navigate("/my-content");
    };

    const handleCreateSubmit = async (values) => {
        setPendingCreateValues(values);
        setShowCreateConfirm(true);
    };

    const handleConfirmCreate = async () => {
        if (!pendingCreateValues) return;

        if (!pendingCreateValues.mediaUrl) {
            showErrorToast("Please upload a file before submitting.");
            setShowCreateConfirm(false);
            setPendingCreateValues(null);
            return;
        }

        const result = await runCreateContent(
            () => createContent({
                title: pendingCreateValues.title,
                platform: pendingCreateValues.platform,
                link: pendingCreateValues.link,
                fileType: pendingCreateValues.fileType,
                postType: pendingCreateValues.postType,
                caption: pendingCreateValues.caption,
                acceptedTerms: pendingCreateValues.acceptedTerms,
                mediaUrl: pendingCreateValues.mediaUrl,
                cloudflareId: pendingCreateValues.cloudflareId,
                thumbnailUrl: pendingCreateValues.thumbnailUrl,
                thumbnailCloudflareId: pendingCreateValues.thumbnailCloudflareId,
            }),
            { errorFallback: "Failed to create content.", silentErrorToast: true }
        );

        setShowCreateConfirm(false);
        setPendingCreateValues(null);
        if (!result) return;
        if (result?.success) {
            setPendingMedia({ cloudflareId: "", thumbnailCloudflareId: "", fileType: "" });
            await loadContent();
            showSuccessToast("Content submitted successfully.");
            setShowSuccess(true);
        } else {
            showErrorToast(result?.error || "Failed to create content.");
        }
    };

    const handleUpdate = async (values) => {
        if (!selectedItem) return;

        const result = await runUpdateContent(
            () =>
                updateContent(Number(selectedItem.id), {
                    title: values.title,
                    platform: values.platform,
                    link: values.link,
                    fileType: values.fileType,
                    postType: values.postType,
                    caption: values.caption,
                    mediaUrl: values.mediaUrl,
                    cloudflareId: values.cloudflareId,
                    thumbnailUrl: values.thumbnailUrl,
                    thumbnailCloudflareId: values.thumbnailCloudflareId,
                }),
            { errorFallback: "Failed to update content.", silentErrorToast: true }
        );

        if (!result) return;
        if (result?.success) {
            setPendingMedia({ cloudflareId: "", thumbnailCloudflareId: "", fileType: "" });
            const targetId = result.contentId ?? selectedItem.id;
            showSuccessToast("Content updated successfully.");
            navigate(`/my-content/${targetId}`);
        } else {
            showErrorToast(result?.error || "Failed to update content.");
        }
    };

    const handleDelete = async () => {
        if (!selectedItem) return;
        const result = await runDeleteContent(
            () => deleteContent(Number(selectedItem.id)),
            { errorFallback: "Failed to delete content.", silentErrorToast: true }
        );
        if (!result) return;
        if (result?.success) {
            setPendingMedia({ cloudflareId: "", thumbnailCloudflareId: "", fileType: "" });
            await loadContent();
            setSelectedItem(null);
            showSuccessToast("Content deleted successfully.");
            navigate("/my-content");
        } else {
            showErrorToast(result?.error || "Failed to delete content.");
        }
    };

    const handleMediaUploaded = async (nextMedia, prevMedia) => {
        setPendingMedia({
            cloudflareId: nextMedia.cloudflareId || "",
            thumbnailCloudflareId: nextMedia.thumbnailCloudflareId || "",
            fileType: nextMedia.fileType || "",
        });

        if (!prevMedia?.cloudflareId) return;
        if (prevMedia.cloudflareId === nextMedia.cloudflareId) return;

        const prevIds = [prevMedia.cloudflareId, prevMedia.thumbnailCloudflareId].filter(Boolean);
        const uniquePrevIds = [...new Set(prevIds)];
        const prevType = prevMedia.fileType === "Video" ? "video" : "image";

        for (const id of uniquePrevIds) {
            try {
                await deleteMedia({
                    cloudflareId: id,
                    type: id === prevMedia.thumbnailCloudflareId ? "image" : prevType,
                });
            } catch {}
        }
    };

    const handleSelect = (item) => {
        navigate(`/my-content/${item.id}`);
    };

    const isEmpty = items.length === 0;

    return {
        navigate,
        items,
        selectedItem,
        isEmpty,
        isLoading,
        isSubmitting,
        isDeleting,
        showSuccess,
        setShowSuccess,
        showCreateConfirm,
        setShowCreateConfirm,
        createLoading: createAction.isLoading,
        openCreate,
        openList,
        cleanupPendingUploads,
        handleCreateSubmit,
        handleConfirmCreate,
        handleUpdate,
        handleDelete,
        handleMediaUploaded,
        handleSelect,
    };
};
